import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CircleHelp } from "lucide-react";

/** Small shared pieces for the registry page. Plain Tailwind, same palette as the rest of the panel. */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      {children}
      {hint && <span className="text-xs text-gray-400">{hint}</span>}
    </div>
  );
}

const inputClass =
  "h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-200 disabled:bg-gray-50 disabled:text-gray-500";

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input {...props} className={`${inputClass} ${props.className ?? ""}`} />
  );
}

export function TextArea(
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>,
) {
  return (
    <textarea
      {...props}
      className={`${inputClass} h-auto py-2 leading-5 ${props.className ?? ""}`}
    />
  );
}

export function SelectInput({
  options,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select {...props} className={`${inputClass} ${props.className ?? ""}`}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

const buttonVariants = {
  primary:
    "bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-300 disabled:text-white shadow-sm",
  outline:
    "border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 disabled:text-gray-400",
  ghost: "text-gray-700 hover:bg-gray-100 disabled:text-gray-400",
} as const;

export function Btn({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof buttonVariants;
  size?: "sm" | "md" | "icon";
}) {
  const sizes = {
    sm: "h-8 px-3 text-sm",
    md: "h-9 px-4 text-sm",
    icon: "size-8",
  };
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-[background-color,transform] active:scale-[0.96] disabled:cursor-not-allowed disabled:active:scale-100 ${buttonVariants[variant]} ${sizes[size]} ${className}`}
    />
  );
}

export function Pill({
  children,
  tone = "gray",
}: {
  children: ReactNode;
  tone?: "gray" | "outline";
}) {
  const tones = {
    gray: "bg-gray-100 text-gray-700",
    outline: "border border-gray-200 text-gray-600",
  };
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-xs ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export function Panel({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-gray-200 bg-white ${className}`}
    >
      {children}
    </div>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
  );
}

/** Slide-over from the right. Closes on the backdrop and Escape. */
export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  footer: ReactNode;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/20 backdrop-blur-[1px]"
      />
      <div
        className="absolute inset-y-0 right-0 flex w-full max-w-lg flex-col bg-white shadow-2xl"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      >
        <div className="border-b border-gray-200 px-6 py-5">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {description && (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          {children}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
          {footer}
        </div>
      </div>
    </div>
  );
}

/** Centered modal. Same shell as the drawer, smaller. */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  footer: ReactNode;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/20 backdrop-blur-[1px]"
      />
      <div className="relative w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="px-6 pt-5">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {description && (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          )}
        </div>
        <div className="flex flex-col gap-4 px-6 py-5">{children}</div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
          {footer}
        </div>
      </div>
    </div>
  );
}

/** A small "?" next to a label. Hover or focus shows the lines, rendered on body so no panel clips it. */
export function Help({
  lines,
  label,
}: {
  lines: Array<[string, string]>;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const rect = anchor.current.getBoundingClientRect();
    const width = 320;
    const left = Math.min(rect.left, window.innerWidth - width - 12);
    setPos({ top: rect.bottom + 6, left: Math.max(12, left) });
  }, [open]);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-label={`About ${label}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex size-5 items-center justify-center rounded align-middle text-gray-400 hover:text-gray-700 focus:text-gray-700 focus:outline-none"
      >
        <CircleHelp className="size-3.5" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: pos.top, left: pos.left, width: 320 }}
            className="pointer-events-none fixed z-50 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 text-left font-normal normal-case shadow-lg"
          >
            {lines.map(([term, meaning]) => (
              <div key={term} className="flex gap-3 py-1 text-xs">
                <span className="w-24 shrink-0 font-medium text-gray-900">
                  {term}
                </span>
                <span className="text-gray-600">{meaning}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
